import React, { useState, useEffect } from 'react';
import { 
    Settings, 
    Globe, 
    Type,
    CheckCircle, 
    AlertCircle, 
    Loader, 
    Save,
    RefreshCw,
    Palette,
    FileText,
    Image,
    Upload,
    Smartphone
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';

const SiteSettings = () => {
    const { user } = useAuth();
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);
    
    const [settings, setSettings] = useState({});
    const [formData, setFormData] = useState({
        siteTitle: '',
        siteDescription: '',
        siteBranding: '',
        aboutText: '',
        ensembleDescription: '',
        siteLogo: '',
        siteIcon: '',
        siteFavicon: '',
        defaultHeaderImage: '',
        defaultHeaderImageMobile: '',
        homePageHeaderImage: '',
        homePageHeaderImageMobile: '',
        profileHeaderImage: '',
        profileHeaderImageMobile: '',
        dashboardHeaderImage: '',
        dashboardHeaderImageMobile: ''
    });

    useEffect(() => {
        fetchSettings();
    }, []);

    const fetchSettings = async () => {
        try {
            setLoading(true);
            setError(null);
            const response = await api.get('/admin/settings');
            
            if (response.data.success) {
                const data = response.data.data;
                setSettings(data);
                
                // Update form data
                setFormData({
                    siteTitle: data.site_title?.value || '',
                    siteDescription: data.site_description?.value || '',
                    siteBranding: data.site_branding?.value || '',
                    aboutText: data.about_text?.value || '',
                    ensembleDescription: data.ensemble_description?.value || '',
                    siteLogo: data.site_logo?.value || '',
                    siteIcon: data.site_icon?.value || '',
                    siteFavicon: data.site_favicon?.value || '',
                    defaultHeaderImage: data.default_header_image?.value || '',
                    defaultHeaderImageMobile: data.default_header_image_mobile?.value || '',
                    homePageHeaderImage: data.homepage_header_image?.value || '',
                    homePageHeaderImageMobile: data.homepage_header_image_mobile?.value || '',
                    profileHeaderImage: data.profile_header_image?.value || '',
                    profileHeaderImageMobile: data.profile_header_image_mobile?.value || '',
                    dashboardHeaderImage: data.dashboard_header_image?.value || '',
                    dashboardHeaderImageMobile: data.dashboard_header_image_mobile?.value || ''
                });
            } else {
                setError('Failed to load site settings');
            }
        } catch (err) {
            console.error('Error fetching site settings:', err);
            setError('Unable to load site settings');
        } finally {
            setLoading(false);
        }
    };

    const handleSave = async () => {
        try {
            setSaving(true);
            setError(null);
            setSuccess(null);

            const settingsToUpdate = {
                site_title: {
                    value: formData.siteTitle,
                    type: 'text',
                    description: 'Main site title displayed in browser and headers'
                },
                site_description: {
                    value: formData.siteDescription,
                    type: 'text',
                    description: 'Site description for meta tags and SEO'
                },
                site_branding: {
                    value: formData.siteBranding,
                    type: 'text',
                    description: 'Short branding tagline for headers and navigation'
                },
                about_text: {
                    value: formData.aboutText,
                    type: 'text',
                    description: 'About section text for the platform'
                },
                ensemble_description: {
                    value: formData.ensembleDescription,
                    type: 'text',
                    description: 'Description of The Deep Wave arts ensemble'
                },
                site_logo: {
                    value: formData.siteLogo,
                    type: 'text',
                    description: 'Square logo image for branding across the site'
                },
                site_icon: {
                    value: formData.siteIcon,
                    type: 'text',
                    description: 'Small icon for headers and navigation (80px recommended)'
                },
                site_favicon: {
                    value: formData.siteFavicon,
                    type: 'text',
                    description: 'Favicon .ico URL used for browser tabs, bookmarks, and search engine results'
                },
                default_header_image: {
                    value: formData.defaultHeaderImage,
                    type: 'text',
                    description: 'Default header background image URL for all pages'
                },
                default_header_image_mobile: {
                    value: formData.defaultHeaderImageMobile,
                    type: 'text',
                    description: 'Default header background image URL for mobile devices'
                },
                homepage_header_image: {
                    value: formData.homePageHeaderImage,
                    type: 'text',
                    description: 'Specific header image for the homepage'
                },
                homepage_header_image_mobile: {
                    value: formData.homePageHeaderImageMobile,
                    type: 'text',
                    description: 'Specific header image for the homepage on mobile devices'
                },
                profile_header_image: {
                    value: formData.profileHeaderImage,
                    type: 'text',
                    description: 'Default header image for user profile pages'
                },
                profile_header_image_mobile: {
                    value: formData.profileHeaderImageMobile,
                    type: 'text',
                    description: 'Default header image for user profile pages on mobile devices'
                },
                dashboard_header_image: {
                    value: formData.dashboardHeaderImage,
                    type: 'text',
                    description: 'Header image for dashboard and admin pages'
                },
                dashboard_header_image_mobile: {
                    value: formData.dashboardHeaderImageMobile,
                    type: 'text',
                    description: 'Header image for dashboard and admin pages on mobile devices'
                }
            };

            const response = await api.put('/admin/settings', {
                settings: settingsToUpdate
            });
            
            if (response.data.success) {
                setSuccess('Site settings updated successfully');
                await fetchSettings(); // Refresh the data
            } else {
                setError(response.data.message || 'Failed to update settings');
            }
        } catch (err) {
            console.error('Error saving site settings:', err);
            setError(err.response?.data?.message || 'Failed to save settings');
        } finally {
            setSaving(false);
        }
    };

    const handleInputChange = (field, value) => {
        setFormData(prev => ({
            ...prev,
            [field]: value
        }));
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-gray-900 pt-20 pb-12">
                <div className="max-w-4xl mx-auto px-6">
                    <div className="glass-card p-8 text-center">
                        <Loader className="w-12 h-12 text-purple-500 mx-auto mb-3 animate-spin" />
                        <p className="text-gray-400">Loading site settings...</p>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-900 pt-20 pb-12">
            <div className="max-w-4xl mx-auto px-6">
                {/* Header */}
                <div className="mb-8">
                    <div className="flex items-center gap-3 mb-2">
                        <Settings className="w-8 h-8 text-blue-400" />
                        <h1 className="text-4xl font-black gradient-text">Site Settings</h1>
                    </div>
                    <p className="text-gray-400">
                        Configure the branding, titles, and descriptions for The Deep Wave platform
                    </p>
                </div>

                {/* Error/Success Messages */}
                {error && (
                    <div className="glass-card p-4 mb-6 border border-red-500/30 bg-red-500/10">
                        <div className="flex items-center gap-2">
                            <AlertCircle className="w-5 h-5 text-red-400" />
                            <span className="text-red-400">{error}</span>
                        </div>
                    </div>
                )}

                {success && (
                    <div className="glass-card p-4 mb-6 border border-green-500/30 bg-green-500/10">
                        <div className="flex items-center gap-2">
                            <CheckCircle className="w-5 h-5 text-green-400" />
                            <span className="text-green-400">{success}</span>
                        </div>
                    </div>
                )}

                {/* Main Settings Form */}
                <div className="glass-card p-8">
                    <div className="space-y-8">
                        {/* Site Title */}
                        <div>
                            <label className="block text-sm font-medium text-gray-300 mb-2">
                                <span className="flex items-center gap-2">
                                    <Type className="w-4 h-4" />
                                    Site Title
                                </span>
                            </label>
                            <input
                                type="text"
                                value={formData.siteTitle}
                                onChange={(e) => handleInputChange('siteTitle', e.target.value)}
                                placeholder="The Deep Wave - Arts and Sound Ensemble"
                                className="w-full px-4 py-3 bg-gray-800 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            />
                            <p className="text-gray-400 text-sm mt-2">
                                Main title displayed in browser tabs, headers, and SEO meta tags
                            </p>
                        </div>

                        {/* Site Description */}
                        <div>
                            <label className="block text-sm font-medium text-gray-300 mb-2">
                                <span className="flex items-center gap-2">
                                    <FileText className="w-4 h-4" />
                                    Site Description
                                </span>
                            </label>
                            <textarea
                                value={formData.siteDescription}
                                onChange={(e) => handleInputChange('siteDescription', e.target.value)}
                                placeholder="A creative arts and sound ensemble platform for artists, musicians, and creators to collaborate, share, and connect."
                                rows={3}
                                className="w-full px-4 py-3 bg-gray-800 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            />
                            <p className="text-gray-400 text-sm mt-2">
                                Description used for SEO meta tags and social media previews
                            </p>
                        </div>

                        {/* Site Branding */}
                        <div>
                            <label className="block text-sm font-medium text-gray-300 mb-2">
                                <span className="flex items-center gap-2">
                                    <Palette className="w-4 h-4" />
                                    Site Branding Tagline
                                </span>
                            </label>
                            <input
                                type="text"
                                value={formData.siteBranding}
                                onChange={(e) => handleInputChange('siteBranding', e.target.value)}
                                placeholder="Arts & Sound Ensemble"
                                className="w-full px-4 py-3 bg-gray-800 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            />
                            <p className="text-gray-400 text-sm mt-2">
                                Short tagline displayed in navigation and headers
                            </p>
                        </div>

                        {/* About Text */}
                        <div>
                            <label className="block text-sm font-medium text-gray-300 mb-2">
                                <span className="flex items-center gap-2">
                                    <Globe className="w-4 h-4" />
                                    About Section Text
                                </span>
                            </label>
                            <textarea
                                value={formData.aboutText}
                                onChange={(e) => handleInputChange('aboutText', e.target.value)}
                                placeholder="Welcome to The Deep Wave, where artists, musicians, and creators come together to collaborate, share their work, and build a vibrant creative community..."
                                rows={5}
                                className="w-full px-4 py-3 bg-gray-800 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            />
                            <p className="text-gray-400 text-sm mt-2">
                                General about text for the platform and landing page
                            </p>
                        </div>

                        {/* Ensemble Description */}
                        <div>
                            <label className="block text-sm font-medium text-gray-300 mb-2">
                                <span className="flex items-center gap-2">
                                    <Globe className="w-4 h-4" />
                                    Ensemble Description
                                </span>
                            </label>
                            <textarea
                                value={formData.ensembleDescription}
                                onChange={(e) => handleInputChange('ensembleDescription', e.target.value)}
                                placeholder="The Deep Wave is a dynamic arts and sound ensemble that brings together talented artists, musicians, performers, and creators from diverse backgrounds..."
                                rows={5}
                                className="w-full px-4 py-3 bg-gray-800 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            />
                            <p className="text-gray-400 text-sm mt-2">
                                Specific description of The Deep Wave arts ensemble
                            </p>
                        </div>

                        {/* Site Logo */}
                        <div>
                            <label className="block text-sm font-medium text-gray-300 mb-2">
                                <span className="flex items-center gap-2">
                                    <Image className="w-4 h-4" />
                                    Site Logo (Square)
                                </span>
                            </label>
                            <input
                                type="url"
                                value={formData.siteLogo}
                                onChange={(e) => handleInputChange('siteLogo', e.target.value)}
                                placeholder="https://example.com/images/logo.png"
                                className="w-full px-4 py-3 bg-gray-800 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                            />
                            <p className="text-gray-400 text-sm mt-2">
                                Square logo image used for branding across the site (recommended: 512x512px or larger square format)
                            </p>
                            {formData.siteLogo && (
                                <div className="mt-3 max-w-[200px]">
                                    <img 
                                        src={formData.siteLogo} 
                                        alt="Site logo preview"
                                        className="w-full aspect-square object-cover rounded-lg border border-gray-600"
                                        onError={(e) => e.target.style.display = 'none'}
                                    />
                                    <p className="text-xs text-gray-500 mt-1 text-center">Square logo preview</p>
                                </div>
                            )}
                        </div>

                        {/* Site Icon */}
                        <div>
                            <label className="block text-sm font-medium text-gray-300 mb-2">
                                <span className="flex items-center gap-2">
                                    <Smartphone className="w-4 h-4" />
                                    Site Icon
                                </span>
                            </label>
                            <input
                                type="url"
                                value={formData.siteIcon}
                                onChange={(e) => handleInputChange('siteIcon', e.target.value)}
                                placeholder="https://example.com/images/icon.png"
                                className="w-full px-4 py-3 bg-gray-800 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                            />
                            <p className="text-gray-400 text-sm mt-2">
                                Small icon used in headers and navigation (recommended: 80x80px or larger)
                            </p>
                            {formData.siteIcon && (
                                <div className="mt-3 max-w-[80px]">
                                    <img
                                        src={formData.siteIcon}
                                        alt="Site icon preview"
                                        className="w-full aspect-square object-cover rounded-lg border border-gray-600"
                                        onError={(e) => e.target.style.display = 'none'}
                                    />
                                    <p className="text-xs text-gray-500 mt-1 text-center">Icon preview</p>
                                </div>
                            )}
                        </div>

                        {/* Favicon */}
                        <div>
                            <label className="block text-sm font-medium text-gray-300 mb-2">
                                <span className="flex items-center gap-2">
                                    <Smartphone className="w-4 h-4" />
                                    Favicon
                                </span>
                            </label>
                            <input
                                type="url"
                                value={formData.siteFavicon}
                                onChange={(e) => handleInputChange('siteFavicon', e.target.value)}
                                placeholder="https://example.com/uploads/site-images/favicon/favicon.ico"
                                className="w-full px-4 py-3 bg-gray-800 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                            />
                            <p className="text-gray-400 text-sm mt-2">
                                The icon shown in browser tabs, bookmarks, and search results (Bing in particular). Generate a proper
                                .ico file from your logo using a tool like realfavicongenerator.net, upload it to the server, then paste its URL here.
                            </p>
                            {formData.siteFavicon && (
                                <div className="mt-3 max-w-[32px]">
                                    <img
                                        src={formData.siteFavicon}
                                        alt="Favicon preview"
                                        className="w-full aspect-square object-cover rounded border border-gray-600"
                                        onError={(e) => e.target.style.display = 'none'}
                                    />
                                    <p className="text-xs text-gray-500 mt-1 text-center whitespace-nowrap">Favicon preview</p>
                                </div>
                            )}
                        </div>

                        {/* Header Images Section */}
                        <div className="border-t border-gray-700 pt-8">
                            <h3 className="text-xl font-semibold text-white mb-6 flex items-center gap-2">
                                <Image className="w-6 h-6 text-purple-400" />
                                Header Images
                            </h3>
                            
                            {/* Default Header Image */}
                            <div className="mb-6">
                                <label className="block text-sm font-medium text-gray-300 mb-2">
                                    <span className="flex items-center gap-2">
                                        <Image className="w-4 h-4" />
                                        Default Header Image
                                    </span>
                                </label>
                                <input
                                    type="url"
                                    value={formData.defaultHeaderImage}
                                    onChange={(e) => handleInputChange('defaultHeaderImage', e.target.value)}
                                    placeholder="https://example.com/images/default-header.jpg"
                                    className="w-full px-4 py-3 bg-gray-800 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                                />
                                <p className="text-gray-400 text-sm mt-2">
                                    Default header background image used across all pages when no specific image is set
                                </p>
                                {formData.defaultHeaderImage && (
                                    <div className="mt-3">
                                        <img 
                                            src={formData.defaultHeaderImage} 
                                            alt="Default header preview"
                                            className="w-full h-24 object-cover rounded-lg border border-gray-600"
                                            onError={(e) => e.target.style.display = 'none'}
                                        />
                                    </div>
                                )}
                            </div>

                            {/* Default Header Image Mobile */}
                            <div className="mb-6">
                                <label className="block text-sm font-medium text-gray-300 mb-2">
                                    <span className="flex items-center gap-2">
                                        <Smartphone className="w-4 h-4" />
                                        Default Header Image (Mobile)
                                    </span>
                                </label>
                                <input
                                    type="url"
                                    value={formData.defaultHeaderImageMobile}
                                    onChange={(e) => handleInputChange('defaultHeaderImageMobile', e.target.value)}
                                    placeholder="https://example.com/images/default-header-mobile.jpg"
                                    className="w-full px-4 py-3 bg-gray-800 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                                />
                                <p className="text-gray-400 text-sm mt-2">
                                    Mobile version of default header (more square/portrait oriented). Falls back to desktop version if not set.
                                </p>
                                {formData.defaultHeaderImageMobile && (
                                    <div className="mt-3">
                                        <div className="max-w-[500px]">
                                            <img 
                                                src={formData.defaultHeaderImageMobile} 
                                                alt="Default mobile header preview"
                                                className="w-full h-32 object-cover rounded-lg border border-gray-600"
                                                onError={(e) => e.target.style.display = 'none'}
                                            />
                                            <p className="text-xs text-gray-500 mt-1">Mobile preview (max 500px)</p>
                                        </div>
                                    </div>
                                )}
                            </div>

                            {/* Homepage Header Image */}
                            <div className="mb-6">
                                <label className="block text-sm font-medium text-gray-300 mb-2">
                                    <span className="flex items-center gap-2">
                                        <Globe className="w-4 h-4" />
                                        Homepage Header Image
                                    </span>
                                </label>
                                <input
                                    type="url"
                                    value={formData.homePageHeaderImage}
                                    onChange={(e) => handleInputChange('homePageHeaderImage', e.target.value)}
                                    placeholder="https://example.com/images/homepage-header.jpg"
                                    className="w-full px-4 py-3 bg-gray-800 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                                />
                                <p className="text-gray-400 text-sm mt-2">
                                    Specific header image for the homepage/landing page
                                </p>
                                {formData.homePageHeaderImage && (
                                    <div className="mt-3">
                                        <img 
                                            src={formData.homePageHeaderImage} 
                                            alt="Homepage header preview"
                                            className="w-full h-24 object-cover rounded-lg border border-gray-600"
                                            onError={(e) => e.target.style.display = 'none'}
                                        />
                                    </div>
                                )}
                            </div>

                            {/* Homepage Header Image Mobile */}
                            <div className="mb-6">
                                <label className="block text-sm font-medium text-gray-300 mb-2">
                                    <span className="flex items-center gap-2">
                                        <Smartphone className="w-4 h-4" />
                                        Homepage Header Image (Mobile)
                                    </span>
                                </label>
                                <input
                                    type="url"
                                    value={formData.homePageHeaderImageMobile}
                                    onChange={(e) => handleInputChange('homePageHeaderImageMobile', e.target.value)}
                                    placeholder="https://example.com/images/homepage-header-mobile.jpg"
                                    className="w-full px-4 py-3 bg-gray-800 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                                />
                                <p className="text-gray-400 text-sm mt-2">
                                    Mobile version of homepage header (more square/portrait oriented). Falls back to desktop or default mobile if not set.
                                </p>
                                {formData.homePageHeaderImageMobile && (
                                    <div className="mt-3">
                                        <div className="max-w-[500px]">
                                            <img 
                                                src={formData.homePageHeaderImageMobile} 
                                                alt="Homepage mobile header preview"
                                                className="w-full h-32 object-cover rounded-lg border border-gray-600"
                                                onError={(e) => e.target.style.display = 'none'}
                                            />
                                            <p className="text-xs text-gray-500 mt-1">Mobile preview (max 500px)</p>
                                        </div>
                                    </div>
                                )}
                            </div>

                            {/* Profile Header Image */}
                            <div className="mb-6">
                                <label className="block text-sm font-medium text-gray-300 mb-2">
                                    <span className="flex items-center gap-2">
                                        <Type className="w-4 h-4" />
                                        Profile Header Image
                                    </span>
                                </label>
                                <input
                                    type="url"
                                    value={formData.profileHeaderImage}
                                    onChange={(e) => handleInputChange('profileHeaderImage', e.target.value)}
                                    placeholder="https://example.com/images/profile-header.jpg"
                                    className="w-full px-4 py-3 bg-gray-800 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                                />
                                <p className="text-gray-400 text-sm mt-2">
                                    Default header image for user profile pages
                                </p>
                                {formData.profileHeaderImage && (
                                    <div className="mt-3">
                                        <img 
                                            src={formData.profileHeaderImage} 
                                            alt="Profile header preview"
                                            className="w-full h-24 object-cover rounded-lg border border-gray-600"
                                            onError={(e) => e.target.style.display = 'none'}
                                        />
                                    </div>
                                )}
                            </div>

                            {/* Profile Header Image Mobile */}
                            <div className="mb-6">
                                <label className="block text-sm font-medium text-gray-300 mb-2">
                                    <span className="flex items-center gap-2">
                                        <Smartphone className="w-4 h-4" />
                                        Profile Header Image (Mobile)
                                    </span>
                                </label>
                                <input
                                    type="url"
                                    value={formData.profileHeaderImageMobile}
                                    onChange={(e) => handleInputChange('profileHeaderImageMobile', e.target.value)}
                                    placeholder="https://example.com/images/profile-header-mobile.jpg"
                                    className="w-full px-4 py-3 bg-gray-800 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                                />
                                <p className="text-gray-400 text-sm mt-2">
                                    Mobile version of profile header (more square/portrait oriented). Falls back to desktop or default mobile if not set.
                                </p>
                                {formData.profileHeaderImageMobile && (
                                    <div className="mt-3">
                                        <div className="max-w-[500px]">
                                            <img 
                                                src={formData.profileHeaderImageMobile} 
                                                alt="Profile mobile header preview"
                                                className="w-full h-32 object-cover rounded-lg border border-gray-600"
                                                onError={(e) => e.target.style.display = 'none'}
                                            />
                                            <p className="text-xs text-gray-500 mt-1">Mobile preview (max 500px)</p>
                                        </div>
                                    </div>
                                )}
                            </div>

                            {/* Dashboard Header Image */}
                            <div className="mb-6">
                                <label className="block text-sm font-medium text-gray-300 mb-2">
                                    <span className="flex items-center gap-2">
                                        <Settings className="w-4 h-4" />
                                        Dashboard Header Image
                                    </span>
                                </label>
                                <input
                                    type="url"
                                    value={formData.dashboardHeaderImage}
                                    onChange={(e) => handleInputChange('dashboardHeaderImage', e.target.value)}
                                    placeholder="https://example.com/images/dashboard-header.jpg"
                                    className="w-full px-4 py-3 bg-gray-800 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                                />
                                <p className="text-gray-400 text-sm mt-2">
                                    Header image for dashboard, admin, and settings pages
                                </p>
                                {formData.dashboardHeaderImage && (
                                    <div className="mt-3">
                                        <img 
                                            src={formData.dashboardHeaderImage} 
                                            alt="Dashboard header preview"
                                            className="w-full h-24 object-cover rounded-lg border border-gray-600"
                                            onError={(e) => e.target.style.display = 'none'}
                                        />
                                    </div>
                                )}
                            </div>

                            {/* Dashboard Header Image Mobile */}
                            <div className="mb-6">
                                <label className="block text-sm font-medium text-gray-300 mb-2">
                                    <span className="flex items-center gap-2">
                                        <Smartphone className="w-4 h-4" />
                                        Dashboard Header Image (Mobile)
                                    </span>
                                </label>
                                <input
                                    type="url"
                                    value={formData.dashboardHeaderImageMobile}
                                    onChange={(e) => handleInputChange('dashboardHeaderImageMobile', e.target.value)}
                                    placeholder="https://example.com/images/dashboard-header-mobile.jpg"
                                    className="w-full px-4 py-3 bg-gray-800 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                                />
                                <p className="text-gray-400 text-sm mt-2">
                                    Mobile version of dashboard header (more square/portrait oriented). Falls back to desktop or default mobile if not set.
                                </p>
                                {formData.dashboardHeaderImageMobile && (
                                    <div className="mt-3">
                                        <div className="max-w-[500px]">
                                            <img 
                                                src={formData.dashboardHeaderImageMobile} 
                                                alt="Dashboard mobile header preview"
                                                className="w-full h-32 object-cover rounded-lg border border-gray-600"
                                                onError={(e) => e.target.style.display = 'none'}
                                            />
                                            <p className="text-xs text-gray-500 mt-1">Mobile preview (max 500px)</p>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Current Values Preview */}
                        <div className="border-t border-gray-700 pt-6">
                            <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                                <Settings className="w-5 h-5 text-blue-400" />
                                Current Site Configuration
                            </h3>
                            
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div className="bg-gray-800/50 p-4 rounded-lg">
                                    <div className="flex items-center gap-2 mb-2">
                                        <Type className="w-4 h-4 text-blue-400" />
                                        <span className="font-medium text-white">Current Title</span>
                                    </div>
                                    <p className="text-sm text-gray-400">
                                        {settings.site_title?.value || 'Not set'}
                                    </p>
                                </div>
                                <div className="bg-gray-800/50 p-4 rounded-lg">
                                    <div className="flex items-center gap-2 mb-2">
                                        <Palette className="w-4 h-4 text-purple-400" />
                                        <span className="font-medium text-white">Current Branding</span>
                                    </div>
                                    <p className="text-sm text-gray-400">
                                        {settings.site_branding?.value || 'Not set'}
                                    </p>
                                </div>
                            </div>
                        </div>

                        {/* Save Button */}
                        <div className="flex gap-4">
                            <button
                                onClick={handleSave}
                                disabled={saving}
                                className="flex items-center gap-2 px-6 py-3 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-600 text-white font-semibold rounded-lg transition-colors"
                            >
                                {saving ? (
                                    <Loader className="w-5 h-5 animate-spin" />
                                ) : (
                                    <Save className="w-5 h-5" />
                                )}
                                {saving ? 'Saving...' : 'Save Settings'}
                            </button>
                            
                            <button
                                onClick={fetchSettings}
                                disabled={loading || saving}
                                className="flex items-center gap-2 px-6 py-3 bg-gray-700 hover:bg-gray-600 disabled:bg-gray-800 text-white font-semibold rounded-lg transition-colors"
                            >
                                <RefreshCw className="w-5 h-5" />
                                Refresh
                            </button>
                        </div>
                    </div>
                </div>

                {/* Help Section */}
                <div className="glass-card p-6 mt-6 border border-blue-500/30">
                    <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                        <Settings className="w-5 h-5 text-blue-400" />
                        Configuration Guidelines
                    </h3>
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                        <div className="space-y-3 text-gray-300">
                            <h4 className="text-white font-semibold">Text Content</h4>
                            <p><strong>Site Title:</strong> Use the full formal name for SEO and official purposes</p>
                            <p><strong>Branding Tagline:</strong> Keep it short for navigation bars and headers</p>
                            <p><strong>Description:</strong> Should reflect the arts ensemble nature and community focus</p>
                            <p><strong>About Text:</strong> Engaging copy that explains the platform's purpose</p>
                            <p><strong>Ensemble Description:</strong> Specific details about The Deep Wave ensemble</p>
                        </div>
                        <div className="space-y-3 text-gray-300">
                            <h4 className="text-white font-semibold">Header Images</h4>
                            <p><strong>Desktop Headers:</strong> Wide landscape images for desktop viewing</p>
                            <p><strong>Mobile Headers:</strong> More square/portrait oriented images for mobile devices</p>
                            <p><strong>Default Header:</strong> Used on all pages when no specific image is set</p>
                            <p><strong>Homepage Header:</strong> Specific to the main landing page</p>
                            <p><strong>Profile Header:</strong> Default for all user profile pages</p>
                            <p><strong>Dashboard Header:</strong> Used on admin and settings pages</p>
                            <p><strong>Desktop Requirements:</strong> High-resolution images (1920x400px recommended)</p>
                            <p><strong>Mobile Requirements:</strong> More square format (800x800px or similar)</p>
                            <p><strong>Fallback:</strong> If mobile version isn't set, desktop version will be used</p>
                            <p><strong>Format:</strong> JPG, PNG, or WebP format with valid HTTPS URLs</p>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default SiteSettings;